import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, AuthUser } from '../services/authService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Logo from '../components/Logo';

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // 監聽認證狀態變化
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user: AuthUser | null) => {
      if (user) {
        console.log('用戶已登入，跳轉到主頁:', user.uid);
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        // 登入
        await authService.signIn(email, password);
        console.log('登入成功');
      } else {
        // 註冊
        if (password !== confirmPassword) {
          throw new Error('密碼確認不匹配');
        }
        if (password.length < 6) {
          throw new Error('密碼長度至少6位');
        }
        await authService.signUp(email, password);
        console.log('註冊成功');
      }
    } catch (error: any) {
      console.error('認證失敗:', error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    
    try {
      await authService.signInWithGoogle();
      console.log('Google登入成功');
    } catch (error: any) {
      console.error('Google登入失敗:', error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--main-background)' }}>
      <div className="max-w-md w-full space-y-8 p-8">
        {/* Logo */}
        <div className="text-center">
          <Logo className="mx-auto h-16 w-auto" />
          <h2 className="mt-6 text-3xl font-bold" style={{ color: 'var(--charcoal)' }}>
            {isLogin ? '歡迎回來' : '加入 SurePal'}
          </h2>
          <p className="mt-2 text-sm" style={{ color: 'var(--medium-gray)' }}>
            {isLogin ? '登入您的帳戶' : '創建您的新帳戶'}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{isLogin ? '登入' : '註冊'}</CardTitle>
            <CardDescription>
              {isLogin ? '輸入您的電子郵件和密碼以登入' : '創建您的SurePal帳戶'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
                  電子郵件
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{
                    borderColor: 'var(--card-shadow)',
                    backgroundColor: 'var(--pure-white)',
                    color: 'var(--charcoal)',
                  }}
                  placeholder="請輸入電子郵件"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
                  密碼
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                  style={{
                    borderColor: 'var(--card-shadow)',
                    backgroundColor: 'var(--pure-white)',
                    color: 'var(--charcoal)',
                  }}
                  placeholder="請輸入密碼"
                />
              </div>

              {!isLogin && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium" style={{ color: 'var(--charcoal)' }}>
                    確認密碼
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="mt-1 block w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                    style={{
                      borderColor: 'var(--card-shadow)',
                      backgroundColor: 'var(--pure-white)',
                      color: 'var(--charcoal)',
                    }}
                    placeholder="請再次輸入密碼"
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
                style={{
                  background: 'var(--charcoal)',
                  border: 'none',
                  borderRadius: '12px'
                }}
              >
                {loading ? '處理中...' : (isLogin ? '登入' : '註冊')}
              </Button>
            </form>

            {/* 分隔線 */}
            <div className="relative mt-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: 'var(--card-shadow)' }} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2" style={{ backgroundColor: 'var(--main-background)', color: 'var(--medium-gray)' }}>
                  或
                </span>
              </div>
            </div>

            {/* Google 登入/註冊按鈕 */}
            <div className="mt-6">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: 'var(--pure-white)',
                  borderColor: 'var(--card-shadow)',
                  color: 'var(--charcoal)'
                }}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {isLogin ? '使用 Google 登入' : '使用 Google 註冊'}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* 切換登入/註冊 */}
        <div className="text-center">
          <button
            type="button"
            onClick={toggleMode}
            className="text-sm font-medium"
            style={{ 
              color: '#001414'
            }}
          >
            {isLogin ? '還沒有帳戶？立即註冊' : '已有帳戶？立即登入'}
          </button>
        </div>

        <div className="text-center">
          <p className="text-xs" style={{ color: 'var(--medium-gray)' }}>
            使用SurePal即表示您同意我們的服務條款和隱私政策
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
